import React, { useMemo, useState } from "react";
import { ChevronDown } from "lucide-react";
import useAmsConfig from "../../hooks/useAmsConfig";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

const BUCKET_TONE = {
  brand: "bg-amber-50 text-amber-800 border-amber-200",
  legal: "bg-rose-50 text-rose-700 border-rose-200",
  editing: "bg-sky-50 text-sky-700 border-sky-200",
};
const LEGACY_TONE = "bg-gray-100 text-gray-600 border-gray-200";

/** code -> { label, bucket, bucketLabel, overridable }; old free-text tags come back as legacy. */
export function useIssueLookup() {
  const { moderationIssues } = useAmsConfig();
  return useMemo(() => {
    const index = {};
    moderationIssues.forEach((b) =>
      b.issues.forEach((i) => {
        index[i.value] = { label: i.label, bucket: b.value, bucketLabel: b.label, overridable: b.overridable };
      })
    );
    return (code) => index[code] ?? { label: String(code), bucket: "", bucketLabel: "", overridable: false };
  }, [moderationIssues]);
}

export const IssueChip = ({ code, onRemove }) => {
  const meta = useIssueLookup()(code);
  return (
    <span
      title={meta.bucketLabel ? `${meta.bucketLabel}: ${meta.label}` : `${meta.label} (old tag)`}
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${BUCKET_TONE[meta.bucket] ?? LEGACY_TONE}`}
    >
      {meta.label}
      {onRemove && (
        <button type="button" onClick={onRemove} className="leading-none opacity-70 hover:opacity-100">
          ×
        </button>
      )}
    </span>
  );
};

/** Read-only chips, coloured by bucket. */
export const IssueChips = ({ codes }) =>
  Array.isArray(codes) && codes.length ? (
    <div className="flex flex-wrap gap-1.5">
      {codes.map((c) => (
        <IssueChip key={c} code={c} />
      ))}
    </div>
  ) : null;

/** Grouped Brand / Legal / Editing Issue picker; old tags already on the row stay until removed. */
export const ModerationIssuePicker = ({ value = [], onChange, disabled = false }) => {
  const { moderationIssues } = useAmsConfig();
  const [filter, setFilter] = useState("");
  const toggle = (code) => onChange(value.includes(code) ? value.filter((v) => v !== code) : [...value, code]);
  const q = filter.trim().toLowerCase();
  return (
    <div className="space-y-2">
      <Popover>
        <PopoverTrigger asChild>
          <Button type="button" size="sm" variant="outline" className="h-7 gap-1 text-xs" disabled={disabled}>
            {value.length ? `${value.length} selected` : "Select issues"}
            <ChevronDown className="h-3 w-3" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-96 space-y-2 p-3">
          <Input placeholder="Filter issues…" value={filter} onChange={(e) => setFilter(e.target.value)} className="h-7 text-sm" />
          <div className="max-h-72 space-y-3 overflow-y-auto">
            {moderationIssues.map((bucket) => {
              const issues = bucket.issues.filter((i) => !q || i.label.toLowerCase().includes(q));
              if (!issues.length) return null;
              return (
                <div key={bucket.value}>
                  <p className="mb-1 flex items-center gap-1.5 px-2 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                    {bucket.label}
                    {bucket.overridable && <span className="font-normal normal-case tracking-normal text-sky-600">· requester can override</span>}
                  </p>
                  {issues.map((i) => (
                    <label key={i.value} className="flex cursor-pointer select-none items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-gray-50">
                      <input type="checkbox" className="accent-primary" checked={value.includes(i.value)} onChange={() => toggle(i.value)} />
                      {i.label}
                    </label>
                  ))}
                </div>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((c) => (
            <IssueChip key={c} code={c} onRemove={disabled ? undefined : () => toggle(c)} />
          ))}
        </div>
      )}
    </div>
  );
};
